import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ProjectDesktop.tsx -- the FDN project shell, rebuilt against design \u00a76.
//
// Layout per \u00a76.1:
//   +-------------------------------------------------+
//   | breadcrumb: org > project > folder | members    |
//   +-------------------------------------------------+
//   |                                                 |
//   |   icons positioned freely on the canvas         |
//   |                                                 |
//   |   [trash, fixed lower-left]      [+, lower-right]
//   |                                                 |
//   |          (hovering chat bubble at cursor)       |
//   |          (assistant window docked lower-right)  |
//   +-------------------------------------------------+
//
// State boundary:
//   - Bootstrap (SSE) populates `ready` once tenant + project + role load.
//   - useFolder owns the catalog tree of the currently-viewed folder; the
//     navigation chain (folderHistory) is local to this component.
//   - useDesktop owns selection / drag / clipboard / preview state and is
//     consumed by Desktop / DesktopIcon / TrashIcon / ContextMenu.
//   - useAgent owns chat conversation + send + hint and is consumed by
//     ChatBubble / AssistantWindow / PlusButton (sets hint).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../utils/api.js';
import { AgentProvider, useChatStore, useChatWindowIds, } from '../hooks/useAgent.js';
import { useDocumentStatusStore, useFloatingStatusIds, } from '../state/documentStatusStore.js';
import { uploadFileLargeCDU, INLINE_UPLOAD_MAX_BYTES, } from '../utils/cduUpload.js';
import { DocumentStatusWindow } from '../components/Documents/DocumentStatusWindow.js';
import { DigestStatusButton } from '../components/Documents/DigestStatusButton.js';
import { ModelSelectorButton } from '../components/Models/ModelSelectorButton.js';
import { DocumentPreview } from '../components/Documents/DocumentPreview.js';
import { FolderSearchBar } from '../components/Search/FolderSearchBar.js';
import { DesktopProvider, useDesktop } from '../hooks/useDesktop.js';
import { useFolder } from '../hooks/useFolder.js';
import { Desktop } from '../components/Desktop/Desktop.js';
import { TrashIcon } from '../components/Desktop/TrashIcon.js';
import { DragOverlay } from '../components/Desktop/DragOverlay.js';
import { ContextMenu } from '../components/Desktop/ContextMenu.js';
import { PlusButton } from '../components/Plus/PlusButton.js';
import { ChatBubble } from '../components/Chat/ChatBubble.js';
import { AssistantWindow } from '../components/Chat/AssistantWindow.js';
import { CoworkersPanel } from '../components/Coworkers/CoworkersPanel.js';
import { MembersPanel } from '../components/Members/MembersPanel.js';
import { MembersDock } from '../components/Members/MembersDock.js';
import { ConversationsPanel } from '../components/Chat/ConversationsPanel.js';
import { AccountChip } from '../components/AccountChip.js';
export function ProjectDesktop() {
    const { projectId } = useParams();
    const nav = useNavigate();
    // ---- bootstrap ----
    const [ready, setReady] = useState(null);
    const [bootstrapErr, setBootstrapErr] = useState(null);
    const [step, setStep] = useState(null);
    const [progressFraction, setProgressFraction] = useState(0);
    useEffect(() => {
        if (!projectId)
            return;
        setReady(null);
        setBootstrapErr(null);
        setStep({ phase: 'connect', label: 'Connecting\u2026' });
        setProgressFraction(0);
        // Bootstrap is a SINGLE request that runs all stages
        // synchronously (per the bolt SDK's synchronous shape -- the
        // goja sandbox doesn't survive request boundaries, so a true
        // SSE stream isn't available). The response carries
        // {role, project, stages?} on success or {error} on failure.
        //
        // The progress phases (connect / install-pack / seed-project
        // / sync-membership / project) come back in stages[]; we
        // animate them by walking the array to keep the splash
        // screen consistent with the express SSE shape.
        let cancelled = false;
        (async () => {
            try {
                const resp = await api.get(`/p/${projectId}/bootstrap`);
                if (cancelled)
                    return;
                if (resp.error) {
                    setBootstrapErr(resp.error);
                    return;
                }
                // Optionally animate through stages quickly so the user
                // sees the chain that ran. Non-blocking.
                if (Array.isArray(resp.stages)) {
                    const total = resp.stages.length;
                    resp.stages.forEach((s, i) => {
                        window.setTimeout(() => {
                            if (cancelled)
                                return;
                            setStep({ phase: s.phase, label: s.label });
                            setProgressFraction((i + 1) / Math.max(total, 1));
                        }, i * 120);
                    });
                }
                if (resp.project) {
                    setReady({ project: resp.project, role: resp.role });
                    setProgressFraction(1);
                }
                else {
                    const r = await api.get(`/p/${projectId}/project`);
                    if (cancelled)
                        return;
                    setReady({ project: r.project, role: resp.role });
                    setProgressFraction(1);
                }
            }
            catch (e) {
                if (cancelled)
                    return;
                setBootstrapErr(e instanceof Error ? e.message : String(e));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId]);
    if (bootstrapErr) {
        return (_jsxs("div", { className: "splash", children: [_jsxs("div", { className: "step errored", children: [_jsx("div", { className: "title", children: "Setup failed" }), _jsx("div", { children: bootstrapErr })] }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsx("button", { onClick: () => window.location.reload(), children: "Retry" }), _jsx("button", { className: "ghost", onClick: () => nav('/orgs'), children: "Back to projects" })] })] }));
    }
    if (!ready || !projectId) {
        return (_jsxs("div", { className: "splash", children: [_jsx("div", { className: "ring" }), _jsxs("div", { className: "step", children: [_jsx("div", { className: "title", children: 'Setting up your FDN desktop\u2026' }), _jsx("div", { children: step?.label ?? '\u00a0' }), _jsx("div", { className: "progress", children: _jsx("div", { className: "bar", style: { width: `${Math.round(progressFraction * 100)}%` } }) })] })] }));
    }
    return (_jsx(DesktopProvider, { children: _jsx(AgentProvider, { projectId: projectId, project: ready.project, role: ready.role, children: _jsx(ProjectShell, { project: ready.project, role: ready.role }) }) }));
}
function ProjectShell({ project, role }) {
    const projectId = project.projectId;
    // URL-based navigation. The route `/p/:projectId/*` catches the rest
    // of the path; we parse it into either:
    //   /p/:projectId                    -> root folder
    //   /p/:projectId/f/:folderId        -> in a folder (deep-linkable)
    //   /p/:projectId/d/:documentId      -> document preview (deep-linkable)
    // The hooks below derive folderHistory + previewItemId FROM the URL,
    // and writing folderHistory updates the URL via navigate(). This
    // gives us bookmarkable URLs and browser back/forward for free.
    const navigate = useNavigate();
    const location = useLocation();
    const urlFolderId = useMemo(() => {
        const m = location.pathname.match(/\/p\/[^/]+\/f\/([^/]+)/);
        return m?.[1];
    }, [location.pathname]);
    const urlDocumentId = useMemo(() => {
        const m = location.pathname.match(/\/p\/[^/]+\/d\/([^/]+)/);
        return m?.[1];
    }, [location.pathname]);
    // Folder history derived from URL. When URL has /f/:folderId we set
    // history to [root, folderId] -- no intermediate breadcrumbs (the
    // breadcrumb data isn't reachable from a single folder ID without
    // walking the tree from root, which would be a separate fetch).
    // Browser back/forward works naturally because each navigate() call
    // pushes a new history entry.
    const initialHistory = useMemo(() => {
        if (urlFolderId && urlFolderId !== project.rootFolderId) {
            return [project.rootFolderId, urlFolderId];
        }
        return [project.rootFolderId];
    }, [urlFolderId, project.rootFolderId]);
    const [folderHistory, setFolderHistory] = useState(initialHistory);
    // Sync state when URL changes externally (back/forward, paste, etc).
    useEffect(() => {
        setFolderHistory(initialHistory);
    }, [initialHistory]);
    const tip = folderHistory[folderHistory.length - 1] ?? project.rootFolderId;
    const isInTrash = tip === project.trashFolderId;
    const folder = useFolder(projectId, tip);
    const trashFolder = useFolder(projectId, project.trashFolderId);
    const trashCount = trashFolder.tree?.entries.length ?? 0;
    // Pull store actions directly. The chatStore is project-global;
    // these functions are stable (referenced via getState() not via
    // closures over selectors) so they don't trigger re-renders.
    const setSideEffectHandlers = useChatStore.getState().setSideEffectHandlers;
    const { selected, clear, selectMany, previewItemId, openPreview, closePreview } = useDesktop();
    // Document deep-link: when URL has /d/:documentId, open the preview.
    // The preview pane reads the document by id; no folder navigation
    // needed (the user can be at any folder when sharing a doc link).
    // We use a ref + effect rather than calling openPreview directly
    // in render to avoid tearing during the URL state-sync window.
    useEffect(() => {
        if (urlDocumentId) {
            openPreview(urlDocumentId);
        }
        // urlDocumentId is the only meaningful dep; openPreview is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlDocumentId]);
    // Side-effect handler registration: when an agent tool's
    // callbacks.onSuccess emits an intent (refresh-folder, navigate-folder,
    // select-item, etc), the agent's reducer routes it here. Each handler
    // wires the intent to the right local primitive: useFolder.refresh for
    // refresh-folder, setFolderHistory for navigation, useDesktop for
    // selection + preview.
    //
    // Refs hold the latest functions so the registration effect can fire
    // once (no re-registration on every state change), but each handler
    // call dereferences the current function. Otherwise the handlers would
    // close over stale folder.refresh / trashFolder.refresh references.
    const folderRefreshRef = useRef(folder.refresh);
    folderRefreshRef.current = folder.refresh;
    const trashRefreshRef = useRef(trashFolder.refresh);
    trashRefreshRef.current = trashFolder.refresh;
    const tipRef = useRef(tip);
    tipRef.current = tip;
    const trashFolderIdRef = useRef(project.trashFolderId);
    trashFolderIdRef.current = project.trashFolderId;
    // Ref to the URL-aware folder navigator so the agent's side-effect
    // handler (registered once below) always calls the current
    // navigateToFolder. Without this, navigateFolder would close over
    // a stale navigate function on the first render.
    const navigateToFolderRef = useRef(() => { });
    useEffect(() => {
        setSideEffectHandlers({
            refreshFolder: (folderId) => {
                // We only have one useFolder instance for the current view; if
                // the agent refreshes a folder we're not viewing, we still want
                // the trash-count badge to update if it was the trash, and we
                // refetch the current view if it happened to be the same id.
                // (Future: if we mount per-folder useFolder instances for
                // splits / previews, dispatch by folderId.)
                if (folderId === tipRef.current) {
                    void folderRefreshRef.current();
                }
                if (folderId === trashFolderIdRef.current) {
                    void trashRefreshRef.current();
                }
            },
            refreshTrash: () => { void trashRefreshRef.current(); },
            navigateFolder: (folderId) => {
                // Drive navigation through the URL so deep-link state stays
                // consistent. Ref dereference picks up the latest helper.
                if (folderId === tipRef.current)
                    return;
                navigateToFolderRef.current(folderId);
            },
            selectItem: (itemId) => { selectMany([itemId]); },
            openPreview,
            closePreview,
            toast: (e) => {
                // Placeholder until the toast hook lands. Console keeps the
                // information visible during the F8/F9 wave.
                // eslint-disable-next-line no-console
                console.log(`[toast:${e.level}]`, e.message);
            },
            openConversationsPanel: () => {
                // The setter is captured by ref further down (declared after
                // this useEffect). Use the ref so this handler stays
                // current across re-renders without re-binding.
                openConversationsPanelRef.current();
            },
        });
    }, [setSideEffectHandlers, selectMany, openPreview, closePreview]);
    // Forward ref to the setter declared below. The state slot itself
    // lives further down (alongside the other panel state); the ref
    // keeps the SideEffectHandlers callback wired up across renders.
    const openConversationsPanelRef = useRef(() => { });
    // context menu state
    const [ctx, setCtx] = useState(null);
    // ---- folder-name cache for breadcrumbs ----
    // Populated whenever we read a folder tree -- we capture the
    // displayName of every catalog-typed entry (sub-folders) from the
    // result. The breadcrumb's labelForFolder consults this cache so user-
    // created folders show their real names rather than a UUID prefix.
    // Map (not state) since recreating it every render is cheap and we
    // want immediate availability without waiting for a setState commit.
    const folderNamesRef = useRef(new Map());
    // Bump a counter when names change so memoised crumbs re-derive.
    const [folderNamesVersion, setFolderNamesVersion] = useState(0);
    useEffect(() => {
        if (!folder.tree)
            return;
        let added = false;
        for (const entry of folder.tree.entries ?? []) {
            if (entry.type === 'catalog' && entry.displayName) {
                const prev = folderNamesRef.current.get(entry.itemId);
                if (prev !== entry.displayName) {
                    folderNamesRef.current.set(entry.itemId, entry.displayName);
                    added = true;
                }
            }
        }
        // Also stash the displayName of the folder itself if useFolder gave
        // us one (the catalog tree's root).
        if (folder.tree.displayName) {
            const prev = folderNamesRef.current.get(tip);
            if (prev !== folder.tree.displayName) {
                folderNamesRef.current.set(tip, folder.tree.displayName);
                added = true;
            }
        }
        if (added)
            setFolderNamesVersion((v) => v + 1);
    }, [folder.tree, tip]);
    // Deep-link landing: when the URL points us to a folder we don't
    // have a cached display name for (no parent walk happened this
    // session), fetch the folder's droplet directly to read its name.
    // The /folders/:id route returns { folder: { name, ... } } -- the
    // catalogTree response's displayName is empty for the tree's root
    // node, so we can't get the name from there. One-shot per folderId
    // we haven't seen.
    useEffect(() => {
        if (tip === project.rootFolderId)
            return;
        if (folderNamesRef.current.has(tip))
            return;
        let cancelled = false;
        void (async () => {
            try {
                const r = await api.get(`/p/${projectId}/folders/${tip}`);
                if (cancelled)
                    return;
                const n = r.folder?.name;
                if (n) {
                    folderNamesRef.current.set(tip, n);
                    setFolderNamesVersion((v) => v + 1);
                }
            }
            catch {
                // best-effort; the breadcrumb just shows the UUID prefix
            }
        })();
        return () => { cancelled = true; };
    }, [tip, project.rootFolderId, projectId]);
    // ---- breadcrumb labels ----
    const crumbs = useMemo(() => {
        return folderHistory.map((fid, i) => ({
            folderId: fid,
            label: labelForFolder(fid, project, i === 0, folderNamesRef.current),
        }));
        // folderNamesVersion participates in the dep list to force
        // re-derivation when the cache mutates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderHistory, project, folderNamesVersion]);
    // ---- push current-folder context to the agent ----
    // The agent provider holds onto the latest location so every chat
    // send carries it (BFF puts it on ToolContext + the system prompt).
    // If the location changed since the last send, the BFF also injects
    // a synthetic "user navigated to X" tool message into the conversation
    // so the model sees the breadcrumb of where the user has been.
    useEffect(() => {
        const tail = crumbs[crumbs.length - 1];
        const path = crumbs.map((c) => c.label);
        useChatStore.getState().setProjectCurrentFolder({
            id: tip,
            name: tail?.label ?? tip.slice(0, 8),
            path,
        });
    }, [tip, crumbs]);
    // ---- canvas handlers ----
    // Navigate by URL change -- the URL is the source of truth for
    // folderHistory, so we navigate() and let the urlFolderId effect
    // resync state. For root, drop back to /p/:projectId (no /f/:id).
    const navigateToFolder = useCallback((folderId) => {
        if (folderId === project.rootFolderId) {
            navigate(`/p/${projectId}`);
        }
        else {
            navigate(`/p/${projectId}/f/${folderId}`);
        }
    }, [navigate, projectId, project.rootFolderId]);
    // Keep the ref current so agent callbacks (registered once on
    // mount) always invoke the latest navigateToFolder closure.
    navigateToFolderRef.current = navigateToFolder;
    const onEnterFolder = (folderId) => {
        clear();
        navigateToFolder(folderId);
    };
    const onPreviewFile = (entry) => {
        // Preview pane (workbench D.6 follow-up). Drive via URL so the
        // /d/:documentId effect handles the openPreview call -- keeps
        // the URL bookmarkable and matches the search-hit-click flow.
        navigate(`/p/${projectId}/d/${entry.itemId}`);
    };
    // Open the document's bytes in a new browser tab. Reuses the
    // /content/private route which 302-redirects to a presigned S3
    // URL -- the new tab follows transparently and Chrome's PDF
    // viewer (or whatever native viewer fits the content type)
    // takes over.
    const onOpenInBrowser = (entry) => {
        const url = `/api/p/${projectId}/documents/${entry.itemId}/content/private`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };
    // Download the document with attachment disposition. The BFF
    // route accepts ?download=1 to flip to attachment Content-
    // Disposition; the browser triggers its native download UI
    // with the original filename.
    const onDownloadFile = (entry) => {
        const url = `/api/p/${projectId}/documents/${entry.itemId}/content/private?download=1`;
        // window.location.assign keeps focus on the current tab and
        // hands the download off to the browser's normal download
        // pipeline. Avoids a flash of about:blank that window.open
        // would create.
        window.location.assign(url);
    };
    /**
     * Upload one file using the appropriate path based on size:
     *
     *   <= INLINE_UPLOAD_MAX_BYTES (10 MB): inline multipart POST
     *      through the BFF. Bytes are buffered in BFF memory then
     *      base64-stuffed into the fdn_documents droplet payload.
     *      Simple, single-roundtrip; suitable for small files.
     *
     *   > INLINE_UPLOAD_MAX_BYTES: CDU (chunked direct upload). Bytes
     *      go BROWSER -> S3 directly via a presigned PUT URL minted by
     *      the BFF. Never touches BFF memory. XHR upload.onprogress
     *      drives a synthesised "upload" stage in the digest-status
     *      panel so the user sees realtime progress.
     *
     * Both paths open a digest-status panel entry on success so the
     * user can watch the rest of the pipeline. The branch decision is
     * MIRRORED on the server: the inline /documents endpoint enforces
     * the same 10 MB cap and 413s anything over (with a message
     * pointing at /reserve). Client + server in sync prevents an
     * accidental BFF memory blowup if a malicious / buggy client
     * sends a huge inline POST.
     */
    const uploadOneFile = async (pid, folderId, f) => {
        if (f.size > INLINE_UPLOAD_MAX_BYTES) {
            // CDU path -- helper drives /reserve, S3 PUT (with progress),
            // and /commit, plus opens the panel + populates upload-progress
            // store as a side effect. We don't need to do anything else
            // here once it returns (or throws).
            await uploadFileLargeCDU(pid, folderId, f);
            return;
        }
        // Inline path. Same shape as before the CDU work landed; the
        // only behaviour difference is the BFF will reject anything
        // over 10 MB now (was 64 MB previously). Files that fall
        // through this branch are guaranteed under the cap on both
        // sides.
        const fd = new FormData();
        fd.append('file', f);
        fd.append('folderId', folderId);
        const r = await api.upload(`/p/${pid}/documents`, fd);
        if (r?.documentId) {
            useDocumentStatusStore.getState().openWindow(r.documentId, r.filename ?? f.name);
        }
    };
    const onUploadFiles = async (files) => {
        for (const f of files) {
            try {
                await uploadOneFile(projectId, tip, f);
            }
            catch (err) {
                // CDU failures surface IN the digest-status panel (the panel
                // entry was opened at /reserve time and uploadProgressStore
                // marked it 'failed' before this throw). Inline-path failures
                // never opened a panel entry, so we still need a fallback;
                // for those we log to console + leave the user to retry.
                // Browser alert() blocks the page and isn't undoable -- the
                // panel's failed-state pill is the right surface for any
                // upload error that has a panel entry.
                // eslint-disable-next-line no-console
                console.error(`Upload failed for ${f.name}:`, err);
            }
        }
        await folder.refresh();
    };
    const onUploadFilesToFolder = async (folderId, files) => {
        for (const f of files) {
            try {
                await uploadOneFile(projectId, folderId, f);
            }
            catch (err) {
                // Same rationale as onUploadFiles: panel surfaces CDU
                // failures via uploadProgressStore.markFailed.
                // eslint-disable-next-line no-console
                console.error(`Upload failed for ${f.name}:`, err);
            }
        }
        await folder.refresh();
    };
    const onMoveEntryToFolder = async (entryId, targetFolderId) => {
        await folder.moveTo(entryId, targetFolderId);
    };
    const onReposition = async (itemId, x, y) => {
        await folder.reposition(itemId, x, y);
    };
    const onContextMenuEntry = (entry, x, y) => {
        setCtx({ x, y, entry });
    };
    const onContextMenuCanvas = (x, y) => {
        setCtx({ x, y });
    };
    const onTrashDropped = async (entryId) => {
        await folder.trashItem(entryId);
        await trashFolder.refresh();
    };
    // ---- + menu hint dispatch ----
    // New folder is a direct, non-AI-mediated operation by default --
    // the user expects clicking "+ New folder" to give them a name input
    // and create a folder, not to start a chat. The assistant path is
    // still available: type "create a folder called X" into the chat
    // bubble and the agent will use fdn_create_folder. Same backend op
    // (POST /api/p/:pid/folders) for both surfaces.
    const [showNewFolder, setShowNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderBusy, setNewFolderBusy] = useState(false);
    const [newFolderError, setNewFolderError] = useState(null);
    const onPlusCreateFolder = () => {
        setNewFolderName('');
        setNewFolderError(null);
        setShowNewFolder(true);
    };
    const onSubmitNewFolder = async () => {
        const name = newFolderName.trim();
        if (!name || newFolderBusy)
            return;
        setNewFolderBusy(true);
        setNewFolderError(null);
        try {
            // POST to the same route the agent's fdn_create_folder calls;
            // tip is the user's currently-viewed folder so the new sub-
            // folder lands where they're standing.
            await api.post(`/p/${projectId}/folders`, { parentFolderId: tip, name });
            setShowNewFolder(false);
            // Refresh the canvas so the new icon appears.
            await folder.refresh();
        }
        catch (err) {
            setNewFolderError(err instanceof Error ? err.message : 'create failed');
        }
        finally {
            setNewFolderBusy(false);
        }
    };
    // Hidden file input the +Upload menu item triggers. Multi-select OK;
    // each file goes through the existing uploadFiles handler that posts
    // to /p/<projectId>/documents with the current folder as the target.
    // The file picker is the canonical Upload action; the assistant hint
    // gets set in parallel so the user can also type "I just uploaded
    // X, can you describe it" if they want.
    const fileInputRef = useRef(null);
    const onFileInputChange = async (e) => {
        const files = Array.from(e.target.files ?? []);
        // Always clear the pending "+ upload" hint when this picker
        // resolves with files. The cancel path is handled separately
        // via the native `cancel` event listener registered in the
        // useEffect below (React's typed props don't expose onCancel).
        useChatStore.getState().setPendingHint(null);
        if (files.length === 0)
            return;
        await onUploadFiles(files);
        // Clear so picking the same filename again still triggers onChange.
        e.target.value = '';
    };
    // Register a native `cancel` listener so dismissing the OS file
    // picker (clicking the picker's Cancel button) also clears the
    // pending "+ upload" hint. React's HTMLInputElement type doesn't
    // expose onCancel as a JSX prop, but the DOM event fires reliably
    // on modern Chrome/Edge. Browsers without support fall back to the
    // Escape handler in ChatBubble.
    useEffect(() => {
        const el = fileInputRef.current;
        if (!el)
            return;
        const onCancel = () => {
            useChatStore.getState().setPendingHint(null);
        };
        el.addEventListener('cancel', onCancel);
        return () => el.removeEventListener('cancel', onCancel);
    }, []);
    const onPlusUpload = () => {
        // Hint is queued at the store level; the next ChatBubble submit
        // (or a + menu choice that opens a window directly) applies it.
        useChatStore.getState().setPendingHint(plusHints.upload);
        // Defer the picker click slightly so the menu has time to close
        // (otherwise some browsers swallow the click as part of the menu
        // dismissal gesture).
        // eslint-disable-next-line no-console
        console.log('[onPlusUpload] fired; will .click() input in 50ms', {
            hasInputRef: !!fileInputRef.current,
            inputDisplay: fileInputRef.current
                ? window.getComputedStyle(fileInputRef.current).display
                : null,
        });
        setTimeout(() => {
            const el = fileInputRef.current;
            // eslint-disable-next-line no-console
            console.log('[onPlusUpload] timer fired', {
                hasInputRef: !!el,
                inputDisplay: el ? window.getComputedStyle(el).display : null,
                userActivation: typeof navigator !== 'undefined' && 'userActivation' in navigator
                    ? {
                        isActive: navigator.userActivation.isActive,
                        hasBeenActive: navigator.userActivation.hasBeenActive,
                    }
                    : 'unsupported',
            });
            el?.click();
        }, 50);
    };
    const onPlusAICreate = () => {
        // Open a fresh fdn window with the hint pre-loaded so the user's
        // first message in that window applies it. This is the explicit
        // "+ AI create document" path -- the bubble's pendingHint flow
        // covers click-anywhere-then-type.
        const wid = useChatStore.getState().openWindow({ recipient: { kind: 'fdn' } });
        useChatStore.getState().setHint(wid, plusHints.aiCreate);
    };
    // ---- coworkers panel ----
    // The 4th + menu item opens a floating panel listing the project's
    // saved AI personas. Click a coworker -> arms useAgent.activeCoworker
    // and pops the chat window for a fresh session anchored to that
    // persona. + New co-worker opens the Edit modal for create.
    const [showCoworkersPanel, setShowCoworkersPanel] = useState(false);
    const onPlusCoWorkers = () => {
        setShowCoworkersPanel(true);
    };
    // ---- members panel ----
    // Lists active project members (humans). Click a row -> arms
    // useAgent.activeMember + opens chat window. The session is created
    // with participants:[{type:'user', id}], routes through the BFF's
    // human-only branch (no agent loop, just message persistence).
    const [showMembersPanel, setShowMembersPanel] = useState(false);
    const onPlusMembers = () => {
        setShowMembersPanel(true);
    };
    // ---- conversations panel ----
    // Cross-session history list (default-FDN + coworker-anchored).
    // Click a row -> useAgent.loadHistory resumes; + New -> default-FDN
    // session.
    const [showConversationsPanel, setShowConversationsPanel] = useState(false);
    const onPlusConversations = () => {
        setShowConversationsPanel(true);
    };
    // Bind the side-effect-handler ref to the setter so AssistantWindow's
    // "+" menu "Conversations" item can open this panel via
    // useChatStore.triggerOpenConversationsPanel().
    openConversationsPanelRef.current = onPlusConversations;
    // ---- context menu actions ----
    const onMenuOpen = async () => {
        if (!ctx?.entry)
            return;
        if (ctx.entry.type === 'catalog')
            onEnterFolder(ctx.entry.itemId);
        else
            onPreviewFile(ctx.entry);
    };
    const onMenuRename = async () => {
        if (!ctx?.entry)
            return;
        const next = window.prompt('New name:', ctx.entry.displayName ?? '');
        if (!next?.trim())
            return;
        await folder.rename(ctx.entry.itemId, next.trim());
    };
    const onMenuDelete = async () => {
        if (!ctx?.entry)
            return;
        await folder.trashItem(ctx.entry.itemId);
        await trashFolder.refresh();
    };
    const onMenuRefresh = async () => { await folder.refresh(); };
    const onMenuShowTrash = () => onEnterFolder(project.trashFolderId);
    return (_jsxs("div", { className: "project-shell", children: [_jsxs("div", { className: "top-bar", children: [crumbs.map((c, i) => {
                        const isTail = i === folderHistory.length - 1;
                        return (_jsxs("span", { className: "crumb-wrap", children: [i > 0 && _jsx("span", { className: "crumb-sep", children: '\u203A ' }), isTail ? (
                                // Current location -- not navigable, just a label.
                                _jsx("span", { className: "crumb crumb-current", "aria-current": "page", children: c.label })) : (
                                // Ancestor crumbs are buttons so they're keyboard-
                                // focusable and announced as interactive by screen
                                // readers. Click navigates back to that depth.
                                _jsx("button", { type: "button", className: "crumb crumb-link", onClick: () => navigateToFolder(c.folderId), children: c.label }))] }, c.folderId));
                    }), _jsx("span", { className: "top-bar-spacer" }), _jsx(FolderSearchBar, { projectId: projectId, folderId: tip, atRoot: tip === project.rootFolderId, scopeLabel: tip === project.rootFolderId
                            ? project.name
                            // Folder displayName resolution order (most-reliable
                            // first): folderNamesRef cache populated from the
                            // PARENT's tree walk (it stores displayName for every
                            // sub-folder catalog entry), folder.tree.displayName
                            // when present (some Phoenix tree responses include it,
                            // others return empty string -- can't rely on it),
                            // then a friendly default. Use || not ?? to coerce
                            // empty strings; Phoenix returns '' for the tree's
                            // own displayName which would short-circuit ??.
                            // folderNamesVersion participates in render via the
                            // crumbs useMemo, so this expression re-runs when the
                            // cache mutates.
                            : (folderNamesRef.current.get(tip)
                                || folder.tree?.displayName
                                || 'this folder'), onOpenHit: (documentId) => {
                            // Update the URL to /p/:projectId/d/:documentId so the
                            // preview is bookmarkable / shareable. The urlDocumentId
                            // effect above will call openPreview when the URL updates.
                            navigate(`/p/${projectId}/d/${documentId}`);
                        } }), _jsx(ModelSelectorButton, { projectId: projectId, role: role }), _jsx(DigestStatusButton, { projectId: projectId }), _jsx("button", { className: "top-bar-action", type: "button", title: "Members (coming soon)", children: "Members" }), _jsx(AccountChip, {})] }), _jsx(MembersDock, { projectId: projectId }), _jsx(Desktop
            // Trash lives in the corner per design \u00a76.1, not on the canvas.
            // We filter it out here so the canvas only shows real content +
            // the still-useful system folders (aidocs, archive).
            , { 
                // Trash lives in the corner per design \u00a76.1, not on the canvas.
                // We filter it out here so the canvas only shows real content +
                // the still-useful system folders (aidocs, archive).
                tree: folder.tree
                    ? {
                        ...folder.tree,
                        entries: folder.tree.entries.filter((e) => e.itemId !== project.trashFolderId),
                    }
                    : null, loading: folder.loading, onEnterFolder: onEnterFolder, onPreviewFile: onPreviewFile, onUploadFiles: onUploadFiles, onUploadFilesToFolder: onUploadFilesToFolder, onMoveEntryToFolder: onMoveEntryToFolder, onReposition: onReposition, onContextMenuEntry: onContextMenuEntry, onContextMenuCanvas: onContextMenuCanvas }), !isInTrash && (_jsx(TrashIcon, { trashCount: trashCount, onClick: () => onEnterFolder(project.trashFolderId), onDropEntry: onTrashDropped })), _jsx(PlusButton, { onCreateFolder: onPlusCreateFolder, onUploadDocument: onPlusUpload, onAICreateDocument: onPlusAICreate, onCoWorkers: onPlusCoWorkers, onMembers: onPlusMembers, onConversations: onPlusConversations }), _jsx(ConversationsPanel, { projectId: projectId, open: showConversationsPanel, onClose: () => setShowConversationsPanel(false) }), _jsx(CoworkersPanel, { projectId: projectId, open: showCoworkersPanel, onClose: () => setShowCoworkersPanel(false) }), _jsx(MembersPanel, { projectId: projectId, open: showMembersPanel, onClose: () => setShowMembersPanel(false) }), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, style: { display: 'none' }, onChange: onFileInputChange }), showNewFolder && (_jsx("div", { className: "modal-bg", onClick: () => !newFolderBusy && setShowNewFolder(false), children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { style: { marginTop: 0 }, children: "New folder" }), _jsxs("p", { style: { color: 'var(--fg-muted)', marginTop: 0 }, children: ["Creates a folder under", ' ', _jsx("strong", { children: labelForFolder(tip, project, tip === project.rootFolderId, folderNamesRef.current) }), "."] }), _jsx("label", { children: "Folder name" }), _jsx("input", { autoFocus: true, value: newFolderName, disabled: newFolderBusy, onChange: (e) => setNewFolderName(e.target.value), onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void onSubmitNewFolder();
                                }
                                else if (e.key === 'Escape') {
                                    if (!newFolderBusy)
                                        setShowNewFolder(false);
                                }
                            }, placeholder: "e.g. Contracts, Q4 research, Personal" }), newFolderError && _jsx("div", { className: "error", children: newFolderError }), _jsxs("div", { style: { display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }, children: [_jsx("button", { className: "ghost", disabled: newFolderBusy, onClick: () => setShowNewFolder(false), children: "Cancel" }), _jsx("button", { disabled: newFolderBusy || !newFolderName.trim(), onClick: () => void onSubmitNewFolder(), children: newFolderBusy ? 'Creating...' : 'Create' })] })] }) })), _jsx(DragOverlay, { folderName: folder.tree?.displayName || labelForFolder(tip, project, tip === project.rootFolderId, folderNamesRef.current) }), _jsx(ChatBubble, {}), _jsx(ChatWindowsHost, {}), _jsx(DocumentStatusWindowsHost, {}), previewItemId && (_jsx(DocumentPreview, { projectId: projectId, documentId: previewItemId, onClose: () => {
                    closePreview();
                    // Drop /d/:documentId from URL. Land back at the current
                    // folder URL (root or sub-folder).
                    if (urlDocumentId) {
                        if (tip === project.rootFolderId) {
                            navigate(`/p/${projectId}`);
                        }
                        else {
                            navigate(`/p/${projectId}/f/${tip}`);
                        }
                    }
                } })), ctx && (_jsx(ContextMenu, { x: ctx.x, y: ctx.y, ...(ctx.entry !== undefined ? { entry: ctx.entry } : {}), role: role, onClose: () => setCtx(null), onPreview: onMenuOpen, 
                // Right-click -> "Open in browser": opens the document
                // bytes in a new tab. The BFF /content/private route
                // 302-redirects to a presigned S3 URL; new tab follows
                // transparently and Chrome's PDF viewer (for PDFs) or
                // the native viewer takes over.
                onOpen: () => { if (ctx.entry)
                    onOpenInBrowser(ctx.entry); }, 
                // Right-click -> "Download": fires the same redirect in
                // the current tab with download=1, which would (with a
                // future raindb change supporting Response-Content-
                // Disposition on the presigned URL) force attachment
                // disposition. Today PDFs render inline regardless --
                // user can click Chrome's download button in the viewer
                // toolbar -- but the menu still works for non-PDF types
                // which the browser downloads natively.
                onDownload: () => { if (ctx.entry)
                    onDownloadFile(ctx.entry); }, onRename: onMenuRename, onDelete: onMenuDelete, onRefresh: onMenuRefresh, onShowTrash: onMenuShowTrash, onNewFolder: onPlusCreateFolder, onUpload: onPlusUpload, onAICreate: onPlusAICreate, onStatus: () => {
                    // Status menu item (workbench I18). Opens the digest
                    // progress window for the right-clicked file. The
                    // CatalogEntry.itemId IS the document UUID (the BFF's
                    // catalog mirrors the document's droplet ID into itemId).
                    // Right-click -> Status spawns a FLOATING window (its own
                    // draggable surface) so the user can keep watching one
                    // specific file while doing other work. Uploads (separate
                    // path above) go into the consolidated panel instead.
                    const entry = ctx?.entry;
                    if (!entry)
                        return;
                    const docId = entry.itemId;
                    const filename = entry.displayName ?? undefined;
                    if (docId) {
                        useDocumentStatusStore.getState()
                            .openWindow(docId, filename, 'floating');
                    }
                } }))] }));
}
function labelForFolder(folderId, project, isFirst, knownNames) {
    if (folderId === project.rootFolderId)
        return isFirst ? project.name : 'Home';
    if (folderId === project.aidocsFolderId)
        return 'AI Docs';
    if (folderId === project.archiveFolderId)
        return 'Archive';
    if (folderId === project.trashFolderId)
        return 'Trash';
    // User-created folders: use the cached displayName if we've seen it
    // (populated when we read a parent's tree). Falls back to a short id
    // suffix only if the cache hasn't seen this folder yet -- shouldn't
    // happen in practice since the user reached the folder by clicking
    // an entry in its parent.
    return knownNames.get(folderId) ?? folderId.slice(0, 8);
}
const plusHints = {
    createFolder: {
        placeholder: 'Type a folder name, or describe what should go in it',
        hidden: 'The user wants to create a folder. Help them name it -- ask if it\'s ambiguous, otherwise call fdn_folder_create with a sensible name.',
        label: '+ folder',
    },
    upload: {
        placeholder: 'Choose a file or describe what to upload',
        hidden: 'The user wants to upload one or more documents. If they describe what they have, suggest where to put it. The actual upload UI is the +Upload button -- nudge them there if needed.',
        label: '+ upload',
    },
    aiCreate: {
        placeholder: 'What kind of document should I create?',
        hidden: 'The user wants the AI to create a new document grounded in their corpus. Ask what kind, then call fdn_document_generate with a clear spec. Save the result into the AI Docs folder unless told otherwise.',
        label: '+ AI create',
    },
};
/**
 * Renders one <AssistantWindow> per windowId in the chatStore registry.
 * Pulled out as its own component so the parent ProjectShell isn't
 * subscribed to windowOrder changes -- only this thin wrapper re-renders
 * when a window is opened/closed.
 */
function ChatWindowsHost() {
    const windowIds = useChatWindowIds();
    return (_jsx(_Fragment, { children: windowIds.map((id) => (_jsx(AssistantWindow, { windowId: id }, id))) }));
}
/**
 * Document status windows host (workbench I18). One DocumentStatusWindow
 * per documentId in the documentStatusStore registry. Auto-opened on
 * upload; user can minimize-to-pill or close. Pill index assignment:
 * minimized windows claim slots in insertion order along the bottom-
 * right strip. The full (non-minimized) window pins to the bottom-right
 * corner; only one full window renders at a time even if multiple are
 * open -- the last-opened wins (others stay minimized as pills).
 */
function DocumentStatusWindowsHost() {
    const docIds = useFloatingStatusIds();
    const windows = useDocumentStatusStore((s) => s.windows);
    // Compute pill indices: each minimized FLOATING window gets a slot
    // in its insertion order. Non-minimized windows don't claim a pill
    // slot. Panel-kind entries don't appear here at all.
    const minimizedDocIds = docIds.filter((id) => windows[`floating:${id}`]?.minimized);
    return (_jsx(_Fragment, { children: docIds.map((id) => {
            const idx = windows[`floating:${id}`]?.minimized
                ? minimizedDocIds.indexOf(id)
                : -1;
            return _jsx(DocumentStatusWindow, { documentId: id, pillIndex: idx }, id);
        }) }));
}
